import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import logger from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAdminBeta = () => {
  const [stats, setStats] = useState(null);
  const [codes, setCodes] = useState([]);
  const [betaUsers, setBetaUsers] = useState([]);
  const [codeFilter, setCodeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');
  const [generateQty, setGenerateQty] = useState(1);
  const [assignees, setAssignees] = useState(['']);
  const [generating, setGenerating] = useState(false);
  const [activeSection, setActiveSection] = useState('codes');
  const token = localStorage.getItem('auth_token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/beta-stats`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (e) { logger.error('Failed to fetch beta stats:', e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCodes = useCallback(async () => {
    try {
      const url = codeFilter === 'all' ? `${API_URL}/api/admin/beta-codes` : `${API_URL}/api/admin/beta-codes?status=${codeFilter}`;
      const res = await fetch(url, { headers });
      if (res.ok) setCodes(await res.json());
    } catch (e) { logger.error('Failed to fetch beta codes:', e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFilter]);

  const fetchBetaUsers = useCallback(async () => {
    try {
      const url = userFilter === 'all' ? `${API_URL}/api/admin/beta-users` : `${API_URL}/api/admin/beta-users?status_filter=${userFilter}`;
      const res = await fetch(url, { headers });
      if (res.ok) setBetaUsers(await res.json());
    } catch (e) { logger.error('Failed to fetch beta users:', e); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFilter]);

  useEffect(() => { fetchStats(); }, [fetchStats]);
  useEffect(() => { fetchCodes(); }, [fetchCodes]);
  useEffect(() => { fetchBetaUsers(); }, [fetchBetaUsers]);

  const updateQuantity = (val) => {
    const qty = Math.max(1, Math.min(100, parseInt(val) || 1));
    setGenerateQty(qty);
    setAssignees(prev => {
      const updated = [...prev];
      while (updated.length < qty) updated.push('');
      return updated.slice(0, qty);
    });
  };

  const updateAssignee = (index, value) => {
    setAssignees(prev => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
  };

  const handleGenerate = async () => {
    if (generateQty < 1 || generateQty > 100) {
      toast.error('Quantity must be between 1 and 100');
      return;
    }
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/beta-codes/generate`, {
        method: 'POST', headers,
        body: JSON.stringify({ quantity: generateQty, assignees: assignees.map(a => a.trim()) })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Generated ${data.generated} beta code(s)`);
        setAssignees(['']);
        setGenerateQty(1);
        fetchStats();
        fetchCodes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to generate codes');
      }
    } catch (e) { toast.error('Network error'); }
    finally { setGenerating(false); }
  };

  const handleRevoke = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/beta-codes/${code}/revoke`, {
        method: 'PUT', headers
      });
      if (res.ok) {
        toast.success(`Code ${code} revoked`);
        fetchStats();
        fetchCodes();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to revoke code');
      }
    } catch (e) { toast.error('Network error'); }
  };

  return {
    stats, codes, betaUsers,
    codeFilter, setCodeFilter,
    userFilter, setUserFilter,
    generateQty, assignees, generating,
    activeSection, setActiveSection,
    updateQuantity, updateAssignee,
    handleGenerate, handleRevoke,
  };
};
